// Chart.js adapters for different chart types
export class ChartAdapters {
  static PIE_CHART_ADAPTER = {
    type: 'pie',
    requiredDimensions: 1,
    requiredMeasures: 1,
    maxDimensions: 1,
    maxMeasures: 1,
    
    validateData(dimensions, measures) {
      return dimensions.length === 1 && measures.length === 1
    },
    
    transformData(reportData, dimensions, measures, options = {}) {
      const dimensionField = dimensions[0]
      const measureField = measures[0]
      
      // Aggregate data by dimension
      const aggregated = new Map()
      
      reportData.forEach(row => {
        const dimensionValue = row[dimensionField] || 'Unknown'
        const measureValue = parseFloat(row[measureField]) || 0
        
        if (aggregated.has(dimensionValue)) {
          aggregated.set(dimensionValue, aggregated.get(dimensionValue) + measureValue)
        } else {
          aggregated.set(dimensionValue, measureValue)
        }
      })
      
      const labels = Array.from(aggregated.keys())
      const data = Array.from(aggregated.values())
      
      return {
        labels,
        datasets: [{
          data,
          backgroundColor: this.generateColors(labels.length, options.colorScheme),
          borderWidth: 1,
          hoverOffset: 4
        }]
      }
    },
    
    getDefaultOptions(customOptions = {}) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: !!customOptions.title,
            text: customOptions.title,
            font: { size: 16, weight: 'bold' }
          },
          legend: {
            display: customOptions.showLegend !== false,
            position: customOptions.legendPosition || 'top'
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || ''
                const value = context.parsed
                const total = context.dataset.data.reduce((a, b) => a + b, 0)
                const percentage = ((value / total) * 100).toFixed(1)
                return `${label}: ${value} (${percentage}%)`
              }
            }
          }
        }
      }
    },
    
    generateColors(count, colorScheme = 'default') {
      return ChartAdapters.generateColorPalette(count, colorScheme)
    }
  }

  static DOUGHNUT_CHART_ADAPTER = {
    ...ChartAdapters.PIE_CHART_ADAPTER,
    type: 'doughnut',
    
    getDefaultOptions(customOptions = {}) {
      const options = ChartAdapters.PIE_CHART_ADAPTER.getDefaultOptions(customOptions)
      
      // Add doughnut-specific options
      options.cutout = '50%'
      
      return options
    }
  }

  static BAR_CHART_ADAPTER = {
    type: 'bar',
    requiredDimensions: 1,
    requiredMeasures: 1,
    maxDimensions: 2,
    maxMeasures: 10,
    
    validateData(dimensions, measures) {
      return dimensions.length >= 1 && measures.length >= 1
    },
    
    transformData(reportData, dimensions, measures, options = {}) {
      const primaryDimension = dimensions[0]
      
      // Group data by primary dimension
      const grouped = new Map()
      
      reportData.forEach(row => {
        const dimensionValue = row[primaryDimension] || 'Unknown'
        
        if (!grouped.has(dimensionValue)) {
          grouped.set(dimensionValue, {})
          measures.forEach(measure => {
            grouped.get(dimensionValue)[measure] = 0
          })
        }
        
        measures.forEach(measure => {
          const measureValue = parseFloat(row[measure]) || 0
          grouped.get(dimensionValue)[measure] += measureValue
        })
      })
      
      const labels = Array.from(grouped.keys())
      const datasets = measures.map((measure, index) => ({
        label: options.fieldMappings?.[measure] || measure.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        data: labels.map(label => grouped.get(label)[measure]),
        backgroundColor: this.generateColors(1, options.colorScheme, index)[0],
        borderColor: this.generateColors(1, options.colorScheme, index)[0],
        borderWidth: 1
      }))
      
      return { labels, datasets }
    },
    
    getDefaultOptions(customOptions = {}) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: !!customOptions.title,
            text: customOptions.title,
            font: { size: 16, weight: 'bold' }
          },
          legend: {
            display: customOptions.showLegend !== false,
            position: customOptions.legendPosition || 'top'
          }
        },
        scales: {
          x: {
            title: {
              display: !!customOptions.xAxisLabel,
              text: customOptions.xAxisLabel
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: !!customOptions.yAxisLabel,
              text: customOptions.yAxisLabel
            }
          }
        }
      }
    },
    
    generateColors(count, colorScheme = 'default', startIndex = 0) {
      return ChartAdapters.generateColorPalette(count, colorScheme, startIndex)
    }
  }

  static LINE_CHART_ADAPTER = {
    type: 'line',
    requiredDimensions: 1,
    requiredMeasures: 1,
    maxDimensions: 2,
    maxMeasures: 10,
    
    validateData(dimensions, measures) {
      return dimensions.length >= 1 && measures.length >= 1
    },
    
    transformData(reportData, dimensions, measures, options = {}) {
      // Use same transformation as bar chart
      const barData = ChartAdapters.BAR_CHART_ADAPTER.transformData(reportData, dimensions, measures, options)
      
      // Modify datasets for line chart
      barData.datasets = barData.datasets.map((dataset, index) => ({
        ...dataset,
        fill: false,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.4 // Smooth curves
      }))
      
      return barData
    },
    
    getDefaultOptions(customOptions = {}) {
      const options = ChartAdapters.BAR_CHART_ADAPTER.getDefaultOptions(customOptions)
      
      // Line chart specific options
      options.elements = {
        line: {
          tension: 0.4
        },
        point: {
          radius: 4,
          hoverRadius: 6
        }
      }
      
      // Remove beginAtZero for line charts to better show trends
      delete options.scales.y.beginAtZero
      
      return options
    },
    
    generateColors(count, colorScheme = 'default', startIndex = 0) {
      return ChartAdapters.generateColorPalette(count, colorScheme, startIndex)
    }
  }

  static AREA_CHART_ADAPTER = {
    ...ChartAdapters.LINE_CHART_ADAPTER,
    type: 'line', // Area charts are line charts with fill: true
    
    transformData(reportData, dimensions, measures, options = {}) {
      const lineData = ChartAdapters.LINE_CHART_ADAPTER.transformData(reportData, dimensions, measures, options)
      
      // Enable fill for area chart
      lineData.datasets = lineData.datasets.map((dataset, index) => ({
        ...dataset,
        fill: index === 0 ? 'origin' : '-1', // Fill to origin for first dataset, to previous for others
        backgroundColor: this.generateColors(1, options.colorScheme, index)[0] + '40' // Add transparency
      }))
      
      return lineData
    },
    
    generateColors(count, colorScheme = 'default', startIndex = 0) {
      return ChartAdapters.generateColorPalette(count, colorScheme, startIndex)
    }
  }

  // Color palette generator
  static generateColorPalette(count, colorScheme = 'default', startIndex = 0) {
    const colorSchemes = {
      default: [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
      ],
      pastel: [
        '#FFB3BA', '#BAFFC9', '#BAE1FF', '#FFFFBA', '#FFDFBA',
        '#E0BBE4', '#957DAD', '#D291BC', '#FEC8D8', '#FFDFD3'
      ],
      vibrant: [
        '#FF5733', '#33FF57', '#3357FF', '#FF33F1', '#F1FF33',
        '#33FFF1', '#F133FF', '#FF8C33', '#8C33FF', '#33FF8C'
      ],
      monochrome: [
        '#2C3E50', '#34495E', '#7F8C8D', '#95A5A6', '#BDC3C7',
        '#ECF0F1', '#D5DBDB', '#AEB6BF', '#85929E', '#5D6D7E'
      ],
      blues: [
        '#E3F2FD', '#BBDEFB', '#90CAF9', '#64B5F6', '#42A5F5',
        '#2196F3', '#1E88E5', '#1976D2', '#1565C0', '#0D47A1'
      ],
      greens: [
        '#E8F5E8', '#C8E6C9', '#A5D6A7', '#81C784', '#66BB6A',
        '#4CAF50', '#43A047', '#388E3C', '#2E7D32', '#1B5E20'
      ]
    }

    const scheme = colorSchemes[colorScheme] || colorSchemes.default
    const colors = []
    
    for (let i = 0; i < count; i++) {
      colors.push(scheme[(startIndex + i) % scheme.length])
    }
    
    return colors
  }

  // Get adapter for chart type
  static getAdapter(chartType) {
    switch (chartType) {
      case 'pie':
        return this.PIE_CHART_ADAPTER
      case 'doughnut':
        return this.DOUGHNUT_CHART_ADAPTER
      case 'bar':
        return this.BAR_CHART_ADAPTER
      case 'line':
        return this.LINE_CHART_ADAPTER
      case 'area':
        return this.AREA_CHART_ADAPTER
      default:
        throw new Error(`Unsupported chart type: ${chartType}`)
    }
  }

  // Validate chart configuration
  static validateChartConfiguration(chartType, dimensions, measures) {
    const adapter = this.getAdapter(chartType)
    return adapter.validateData(dimensions, measures)
  }

  // Get chart requirements
  static getChartRequirements(chartType) {
    const adapter = this.getAdapter(chartType)
    return {
      type: chartType,
      requiredDimensions: adapter.requiredDimensions,
      requiredMeasures: adapter.requiredMeasures,
      maxDimensions: adapter.maxDimensions,
      maxMeasures: adapter.maxMeasures
    }
  }

  // Transform data using appropriate adapter
  static transformDataForChart(chartType, reportData, dimensions, measures, options = {}) {
    const adapter = this.getAdapter(chartType)
    return adapter.transformData(reportData, dimensions, measures, options)
  }

  // Get default options for chart type
  static getDefaultOptions(chartType, customOptions = {}) {
    const adapter = this.getAdapter(chartType)
    return adapter.getDefaultOptions(customOptions)
  }

  // Get suitable chart types for given data structure
  static getSuitableChartTypes(dimensions, measures) {
    const suitableTypes = []
    
    // Pie/Doughnut charts
    if (dimensions.length === 1 && measures.length === 1) {
      suitableTypes.push('pie', 'doughnut')
    }
    
    // Bar/Line/Area charts
    if (dimensions.length >= 1 && measures.length >= 1) {
      suitableTypes.push('bar', 'line', 'area')
    }
    
    return suitableTypes
  }

  // Chart interaction utilities
  static addDrillDownCapability(chart, onDrillDown) {
    chart.options.onClick = (event, elements) => {
      if (elements.length > 0) {
        const element = elements[0]
        const datasetIndex = element.datasetIndex
        const index = element.index
        const label = chart.data.labels[index]
        const value = chart.data.datasets[datasetIndex].data[index]
        
        if (typeof onDrillDown === 'function') {
          onDrillDown({
            label,
            value,
            datasetIndex,
            index,
            dataset: chart.data.datasets[datasetIndex]
          })
        }
      }
    }
  }

  // Export utilities
  static exportChartAsImage(chart, format = 'png') {
    if (!chart) return null
    
    return chart.toBase64Image(`image/${format}`, 1.0)
  }

  static getChartImageData(chart) {
    if (!chart) return null
    
    return {
      dataUrl: chart.toBase64Image(),
      width: chart.width,
      height: chart.height,
      aspectRatio: chart.aspectRatio
    }
  }
}